import { useMemo } from 'react'
import { Box, Stack, Typography, CircularProgress } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ChecklistTemplateSummary } from '@cmms/types'
import { ApiError } from '@cmms/api-client'

import { api } from '../../lib/api'
import { apiClient } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { SearchableSelect } from '../../components/SearchableSelect'

/// Shape returned by GET /api/v1/risk-category-checklists — one row
/// per fixed risk category (see api/src/lib/risk-categories.js),
/// regardless of whether it's linked to a checklist yet.
interface RiskCategoryMapping {
  key: string
  label: string
  checklist_template_id: string | null
  checklist_name: string | null
  checklist_status: string | null
}

/**
 * Links each fixed HSE risk category to a published checklist
 * template. When a public reporter submits under a linked category,
 * they get a follow-up email with a link to fill out that checklist
 * themselves (see public-checklist-fill.routes.js on the backend).
 */
export function RiskCategoriesTab() {
  const qc = useQueryClient()

  const mappingsQ = useQuery({
    queryKey: ['risk-category-checklists'],
    queryFn: async () => (await apiClient.get<RiskCategoryMapping[]>('/api/v1/risk-category-checklists')).data,
  })
  const mappings = Array.isArray(mappingsQ.data) ? mappingsQ.data : []

  // Only published templates are linkable — an unpublished one would
  // 422 on the backend the moment a category tried to use it, so keep
  // the picker itself from offering that option in the first place.
  const templatesQ = useQuery({
    queryKey: ['checklists', { status: 'published' }],
    queryFn: () => api.checklists.list({ status: 'published' }),
    staleTime: 30_000,
  })
  const templates: ChecklistTemplateSummary[] = Array.isArray(templatesQ.data) ? templatesQ.data : []

  const setMappingM = useMutation({
    mutationFn: ({ categoryKey, checklistTemplateId }: { categoryKey: string; checklistTemplateId: string | null }) =>
      apiClient.put(`/api/v1/risk-category-checklists/${categoryKey}`, { checklist_template_id: checklistTemplateId }),
    onSuccess: () => {
      toast.success('Updated')
      qc.invalidateQueries({ queryKey: ['risk-category-checklists'] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to update'),
  })

  const isLoading = mappingsQ.isLoading || templatesQ.isLoading

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', py: 3, px: 2 }}>
      <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 0.5 }}>Risk Categories</Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 3 }}>
        Link a checklist to a risk category — when someone submits a public report under that
        category, they'll get a follow-up email with a link to fill it out themselves. Only
        published checklists can be linked.
      </Typography>

      {isLoading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={22} /></Box>
      ) : (
        <Stack spacing={1}>
          {mappings.map((m) => (
            <RiskCategoryRow
              key={m.key}
              mapping={m}
              templates={templates}
              saving={setMappingM.isPending && setMappingM.variables?.categoryKey === m.key}
              onChange={(checklistTemplateId) => setMappingM.mutate({ categoryKey: m.key, checklistTemplateId })}
            />
          ))}
        </Stack>
      )}
    </Box>
  )
}

function RiskCategoryRow({
  mapping, templates, saving, onChange,
}: {
  mapping: RiskCategoryMapping
  templates: ChecklistTemplateSummary[]
  saving: boolean
  onChange: (checklistTemplateId: string | null) => void
}) {
  // The currently-linked template might have been archived/unpublished
  // since it was linked — still show it as an option (read-only-ish,
  // via its own summary) so the picker doesn't silently blank out a
  // real link the admin hasn't consciously changed yet.
  const options = useMemo(() => {
    if (mapping.checklist_template_id && !templates.some((t) => t.id === mapping.checklist_template_id)) {
      return [
        { id: mapping.checklist_template_id, name: `${mapping.checklist_name ?? 'Unknown'} (${mapping.checklist_status ?? 'unavailable'})` } as ChecklistTemplateSummary,
        ...templates,
      ]
    }
    return templates
  }, [templates, mapping])

  return (
    <Stack
      direction="row" alignItems="center" spacing={2}
      sx={{ px: 2, py: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{mapping.label}</Typography>
      </Box>
      <Box sx={{ width: 320, flexShrink: 0 }}>
        <SearchableSelect
          value={mapping.checklist_template_id ?? ''}
          onChange={(id) => onChange(id || null)}
          options={options}
          getValue={(t) => t.id}
          getLabel={(t) => t.name}
          placeholder="No checklist linked"
          disabled={saving}
          size="small"
          fullWidth
        />
      </Box>
    </Stack>
  )
}
